// GENERATED FILE — DO NOT EDIT.
// Source: backend/src/services/processingFee.ts
// Regenerate: node scripts/sync-processing-fee.mjs
// Edits here are discarded; scripts/__tests__/sync-processing-fee.test.mjs fails on drift.
// (website/client/src/lib/processingFee.ts)

/**
 * @description Processing-fee coverage — how much a donor must add so the charity nets the
 *   full gift after Stripe takes its cut. CANONICAL SOURCE for every surface that quotes or
 *   charges this number.
 * @since 2.0.0
 *
 * WHY A GROSS-UP AND NOT A SURCHARGE. Stripe's fee is 2.9% + $0.30 of the amount it actually
 * CHARGES, which is the gift plus whatever the donor added to cover the fee. Charging
 * `gift * 0.029 + 0.30` therefore under-collects, because the percentage is applied to the
 * larger number than the one it was computed from. Measured on staging 2026-08-30: a $1,000
 * gift collected $29.30, Stripe charged 1029.30 * 0.029 + 0.30 = $30.15 on the $1,029.30 total,
 * and the charity netted $999.15 against an intended $1,000.
 *
 * Solving `total - (total * P + F) = gift` for the total gives the amount whose fee it exactly
 * absorbs:
 *
 *   total = (gift + F) / (1 - P)
 *
 * At $1,000 that is $1,030.18: Stripe takes 1030.18 * 0.029 + 0.30 = $30.18 and $1,000.00 lands.
 *
 * WHY THIS FILE IS COPIED, NOT IMPORTED. Three build units quote this number — the backend
 * (which creates the Stripe session and so decides the charge), the marketing site's donate form
 * (which shows the donor the fee before they submit), and the legacy donation-relay service. They
 * have separate build contexts: the backend image is built from `./backend`, so a repo-level
 * shared module is not on disk inside it. `scripts/sync-processing-fee.mjs` mirrors this file into
 * the other two and `scripts/__tests__/sync-processing-fee.test.mjs` fails if a mirror drifts —
 * the same "one source, generated copies, gated in CI" pattern the DAL uses for its bootstrap
 * scripts. Edit THIS file and re-run the sync; never edit a mirror.
 */

/** Stripe's percentage component for a standard US card charge. */
export const STRIPE_PERCENT_RATE = 0.029;

/** Stripe's fixed per-charge component, in dollars. */
export const STRIPE_FIXED_FEE = 0.3;

/**
 * @description The amount a donor adds so that `gift` survives Stripe's cut intact.
 * @param gift - The intended donation, in DOLLARS.
 * @returns The fee coverage in dollars, rounded to the cent. `0` for a non-positive gift.
 * @example calculateProcessingFee(1000) === 30.18   // charge $1,030.18, charity nets $1,000.00
 */
export function calculateProcessingFee(gift: number): number {
  if (!Number.isFinite(gift) || gift <= 0) return 0;
  return Math.round((calculateTotalWithFee(gift) - gift) * 100) / 100;
}

/**
 * @description What the card is actually charged when the donor covers the fee.
 * @param gift - The intended donation, in DOLLARS.
 * @returns `gift + calculateProcessingFee(gift)`, rounded to the cent.
 * @remarks Derived from the rounded fee rather than from the raw quotient so the total, the fee
 *   and the gift always agree to the cent — a total computed independently can land a cent off
 *   the two line items Stripe is sent.
 */
export function calculateTotalWithFee(gift: number): number {
  if (!Number.isFinite(gift) || gift <= 0) return 0;
  const raw = (gift + STRIPE_FIXED_FEE) / (1 - STRIPE_PERCENT_RATE);
  return Math.round(raw * 100) / 100;
}
